// The two grey text colours are as light as WCAG AA allows and no lighter.
// They used to be #6B7280 and #9CA3AF, which put the small print on the chat
// screen (「AIの回答はご確認ください」) at 2.4:1 against the page — below the
// 4.5:1 that body text has to clear, and a difficulty before it is a style.
// The hierarchy is kept by darkening both: primary 17:1, secondary 7.2:1,
// muted 5.5:1 on #FAFAFA, and muted still clears 4.5:1 on the chat's own
// surfaces (#F0F4F9, #E8F0FE), which are the lightest backgrounds it lands on.
export const colors = {
  bg: '#FAFAFA',
  surface: '#FFFFFF',
  border: '#E5E7EB',
  borderSubtle: '#F3F4F6',
  textPrimary: '#111827',
  textSecondary: '#4B5563',
  textMuted: '#5F6775',
  accent: '#2563EB',
  accentHover: '#1D4ED8',
  accentBg: '#EFF6FF',
  accentBorder: '#BFDBFE',
  // Same rule as the greys: these two are read as words («送信しました»,
  // 「0/100」), so they have to clear 4.5:1 on white. #059669 / #D97706 did
  // not (3.8 and 3.2). The -Bg and -Border pairs below are surfaces, not
  // text, and keep their lightness.
  success: '#047857',
  successBg: '#F0FDF4',
  successBorder: '#BBF7D0',
  warning: '#B45309',
  warningBg: '#FFFBEB',
  warningBorder: '#FCD34D',
  danger: '#DC2626',
  dangerHover: '#B91C1C',
  dangerBg: '#FEF2F2',
  dangerBorder: '#FECACA',
} as const

// The sidebar is the one dark surface in the app, and it is dark on purpose:
// it is the only part of the screen that never changes, so letting it recede
// is what keeps the eye on the page beside it. A white rail and a white page
// separated by one hairline put both at the same weight, and the nav won a
// contest it should not have entered.
//
// These are its own colours because nothing else in the app sits on a dark
// background — `colors.textSecondary` and friends are tuned for white and are
// unreadable here. Contrast against `sidebar.bg`: text 8.9:1, textMuted
// 4.7:1, white on `bgActive` 11.4:1.
export const sidebar = {
  bg: '#222135',
  /** The active row, and any raised panel inside the rail. */
  bgActive: '#3C3651',
  bgHover: 'rgba(255, 255, 255, 0.06)',
  /** Dividers inside the rail. The rail's own right edge needs none: the
      colour change is the edge. */
  border: 'rgba(255, 255, 255, 0.10)',
  borderStrong: 'rgba(255, 255, 255, 0.16)',
  text: '#C4C0D4',
  textActive: '#FFFFFF',
  /** Group headings, the signed-in address, and the notification notes. */
  textMuted: '#8E89A6',
} as const

export const space = {
  0: '0',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
} as const

export const radius = {
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  full: '9999px',
} as const

export const shadow = {
  sm: '0 1px 2px rgba(0,0,0,0.05)',
  md: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
  lg: '0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.04)',
  xl: '0 20px 40px rgba(0,0,0,0.15)',
} as const

export const font = {
  size: {
    xs: '12px',
    sm: '13px',
    base: '14px',
    md: '15px',
    lg: '16px',
    xl: '18px',
    '2xl': '20px',
    '3xl': '24px',
  },
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.625,
  },
} as const

export const layout = {
  sidebarWidth: '220px',
} as const

// エリアの色。画面のどの区画かを言うためのもので、**面は塗らない**: 上辺の
// 2pxの罫と見出しの文字にだけ使う。
//
// 面まで塗れないのは、この画面の赤・琥珀・緑がすでに判定の意味を持っている
// からで（✗=赤・△=琥珀・通った=緑、率の横棒も同じ色）、成績の区画を琥珀で
// 塗ると、落ちていない項目まで警告に見える。罫だけなら、面の中では今までどおり
// 赤が「直すところ」のままでいられる。
//
// `log` だけパレットの外の灰。罫は文字ではないので 4.5:1 の対象ではない。
export const area = {
  /** 押すところ（入力・ボタン） */
  input: colors.accent,
  /** 手紙そのもの */
  mail: colors.textPrimary,
  /** 判定（文面チェック） */
  checks: colors.danger,
  /** 材料・事実（調べたこと） */
  research: colors.success,
  /** 数（成績） */
  numbers: colors.warning,
  /** 機械の記録（段ごと・履歴） */
  log: '#9CA3AF',
} as const

export const tokens = { colors, space, radius, shadow, font, layout, area }
